/* eslint-disable import/prefer-default-export */
import { ref, computed, watch } from '@vue/composition-api'
import moment from 'moment'
import { parseRequestError } from '@/helpers/helpers'
import i18n from '@/libs/i18n'
import { orderRequests } from '@/http/requests/order/order'
import {
  mapPlannerResponseToState,
  createPlannerQuery,
  resolveEventDurationMinutes,
} from '@/views/orders/Planner/setup'

export const usePlannerData = (selectedDate, config) => {
  const plannerState = ref(mapPlannerResponseToState({
    date: selectedDate.value,
    teamDefinitions: [],
    unplannedOrders: [],
    plannedOrders: [],
  }))
  const loading = ref(false)
  const backlogRenderVersion = ref(0)

  const backlogRenderKey = computed(() => `${selectedDate.value}:${backlogRenderVersion.value}:${plannerState.value.backlogItems.length}`)

  const applyEditableTimeState = events => events.map(event => ({
    ...event,
    editable: !event.original?.isAssigned,
    dragInTime: !event.original?.isAssigned,
    resize: !event.original?.isAssigned,
  }))

  const resolvePlannerEventOrderId = event => String(event?.orderId || event?.original?.orderId || '')

  const isSamePlannerEvent = (leftEvent, rightEvent) => {
    const leftId = String(leftEvent?.id || '')
    const rightId = String(rightEvent?.id || '')
    const leftOrderId = resolvePlannerEventOrderId(leftEvent)
    const rightOrderId = resolvePlannerEventOrderId(rightEvent)
    return (leftId && rightId && leftId === rightId)
      || (leftOrderId && rightOrderId && leftOrderId === rightOrderId)
  }

  const createOptimisticPlannerEvent = event => {
    const existingEvent = plannerState.value.events.find(item => isSamePlannerEvent(item, event))
    const original = { ...(existingEvent?.original || {}), ...(event?.original || {}) }
    const orderId = original.orderId || event?.orderId
    return {
      ...(existingEvent || {}),
      ...event,
      id: String(event?.id || existingEvent?.id || orderId),
      orderId,
      resource: event?.resource ? String(event.resource) : undefined,
      color: event?.color || original.color || existingEvent?.color,
      editable: !original.isAssigned,
      dragInTime: !original.isAssigned,
      resize: !original.isAssigned,
      original: {
        ...original,
        orderId,
        durationMinutes: resolveEventDurationMinutes(event),
      },
    }
  }

  const applyOptimisticPlannerEvent = event => {
    const optimisticEvent = createOptimisticPlannerEvent(event)
    const nextEvents = plannerState.value.events
      .filter(item => !isSamePlannerEvent(item, optimisticEvent))
    nextEvents.push(optimisticEvent)
    nextEvents.sort((left, right) => moment(left.start).valueOf() - moment(right.start).valueOf())
    plannerState.value = {
      ...plannerState.value,
      backlogItems: plannerState.value.backlogItems
        .filter(item => !isSamePlannerEvent(item, optimisticEvent)),
      events: nextEvents,
    }
  }

  const fetchPlannerData = () => {
    loading.value = true
    return orderRequests.getOrderPlanner(createPlannerQuery({ date: selectedDate.value }))
      .then(response => {
        const nextState = mapPlannerResponseToState(response.data)
        const current = plannerState.value.resources
        const next = nextState.resources
        const resourcesUnchanged = current.length === next.length
          && current.every((r, i) => r.id === next[i].id)
        plannerState.value = {
          ...nextState,
          events: applyEditableTimeState(nextState.events),
          resources: resourcesUnchanged ? current : next,
        }
        config.applyAutomaticViewMode(nextState.resources.length)
        backlogRenderVersion.value += 1
      })
      .catch(error => parseRequestError(error))
      .finally(() => { loading.value = false })
  }

  watch(selectedDate, fetchPlannerData, { immediate: true })

  const executeOrderRequest = requestFn => {
    loading.value = true
    return requestFn()
      .then(async () => {
        if (window?.toast?.notify?.success) {
          window.toast.notify.success(i18n.t('order.planning.notify.updated'))
        }
        await fetchPlannerData()
      })
      .catch(error => {
        parseRequestError(error)
        return fetchPlannerData()
      })
      .finally(() => { loading.value = false })
  }

  const savePlanning = (orderId, payload) => executeOrderRequest(() => orderRequests.upsertOrderPlanning(orderId, payload))

  const patchOrder = (orderId, payload) => executeOrderRequest(() => orderRequests.patchOrder(orderId, payload))

  const removePlanning = orderId => executeOrderRequest(() => orderRequests.deleteOrderPlanning(orderId))

  return {
    plannerState,
    loading,
    backlogRenderKey,
    fetchPlannerData,
    applyOptimisticPlannerEvent,
    savePlanning,
    patchOrder,
    removePlanning,
  }
}
